import React, { forwardRef, useMemo, useCallback, useState, useRef } from 'react';
import { TableVirtuoso, VirtuosoHandle } from 'react-virtuoso';
import {
  Table as MuiTable,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableBody as MuiTableBody,
  Box,
} from '@mui/material';
import {
  colors,
  IconButton,
  Tooltip,
  __SWEEP_DESIGN_TYPOGRAPHY_STYLES___,
} from '@sweep-io/sweep-design';
import { Copy, Check } from '@sweep-io/sweep-design/dist/icons';
import { TableProps } from 'react-virtuoso';
import { ParsedMarkdownTable } from './parseMarkdownTable';
import { DataTableVariant } from '../table/TableTypes';
import { dataTableVariants } from '../table/dataTableVariants';
import { telemetry } from '../../../telemetry';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { ClampedText } from '../ClampedText';

const MAX_LINES = 3;

interface VirtualizedMarkdownTableProps {
  parsedTable: ParsedMarkdownTable;
  variant?: DataTableVariant;
  /** Original markdown for copy functionality */
  originalMarkdown?: string;
}

/**
 * Generate HTML table from parsed table data for clipboard
 */
function generateHtmlTable(headers: string[], rows: string[][]): string {
  const headerCells = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const headerRow = `<thead><tr>${headerCells}</tr></thead>`;

  const bodyRows = rows
    .map((row) => {
      const cells = row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  const body = `<tbody>${bodyRows}</tbody>`;

  return `<table>${headerRow}${body}</table>`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Copy button component for the virtualized table
 * Copies as both HTML (for rich paste) and plain text (markdown format)
 */
const VirtualizedTableCopyButton = ({
  markdown,
  headers,
  rows,
}: {
  markdown: string;
  headers: string[];
  rows: string[][];
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      const htmlContent = generateHtmlTable(headers, rows);
      const clipboardData = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([markdown], { type: 'text/plain' }),
      });

      await navigator.clipboard.write([clipboardData]);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Fallback to plain text
      try {
        await navigator.clipboard.writeText(markdown);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (error) {
        telemetry.captureError(error);
      }
    }
  }, [markdown, headers, rows]);

  return (
    <Tooltip title={isCopied ? 'Copied' : 'Copy table'} placement="top">
      <span>
        <IconButton onClick={handleCopy} size="tiny" variant="flat">
          {isCopied ? <Check color={colors.grey[800]} /> : <Copy />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

/**
 * Virtualized table component for rendering large markdown tables efficiently.
 */
export const VirtualizedMarkdownTable: React.FC<VirtualizedMarkdownTableProps> = ({
  parsedTable,
  variant = DataTableVariant.medium,
  originalMarkdown,
}) => {
  const { headers, rows } = parsedTable;
  const variantProps = dataTableVariants[variant];
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Calculate fixed column width to prevent shifting during scroll
  const columnWidth = useMemo(() => {
    if (headers.length === 0) return '100%';
    return `${100 / headers.length}%`;
  }, [headers.length]);

  const cellStyles = useMemo(
    () => ({
      fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[variantProps.fontVariant]?.fontSize,
      lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[variantProps.fontVariant]?.lineHeight,
      fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[variantProps.fontVariant]?.fontWeight,
      padding: variantProps.cellPadding,
      borderBottom: `1px solid ${colors.grey[120]}`,
      verticalAlign: 'top',
    }),
    [variantProps],
  );

  const headerCellStyles = useMemo(
    () => ({
      ...cellStyles,
      backgroundColor: colors.grey[100],
      color: variantProps.headerFontColor,
      position: 'sticky' as const,
      top: 0,
      zIndex: 1,
    }),
    [cellStyles, variantProps.headerFontColor],
  );

  // Table components for TableVirtuoso
  const components = useMemo(() => {
    const Table = forwardRef<HTMLTableElement, TableProps>(({ children, style }, ref) => (
      <MuiTable
        ref={ref}
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          tableLayout: 'fixed',
        }}
        style={
          {
            '--virtuosoPaddingTop': (style?.paddingTop ?? 0) + 'px',
            '--virtuosoPaddingBottom': (style?.paddingBottom ?? 0) + 'px',
          } as React.CSSProperties
        }
      >
        {children}
      </MuiTable>
    ));

    const TableHead = forwardRef<
      HTMLTableSectionElement,
      React.HTMLAttributes<HTMLTableSectionElement>
    >(({ children, ...props }, ref) => (
      <MuiTableHead ref={ref} {...props}>
        {children}
      </MuiTableHead>
    ));

    const TableBody = forwardRef<
      HTMLTableSectionElement,
      React.HTMLAttributes<HTMLTableSectionElement>
    >(({ children, ...props }, ref) => (
      <MuiTableBody
        ref={ref}
        {...props}
        sx={{
          position: 'relative',
        }}
      >
        {children}
      </MuiTableBody>
    ));

    const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
      (props, ref) => (
        <MuiTableRow
          ref={ref}
          {...props}
          sx={{
            cursor: 'default',
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: colors.grey[100],
            },
            '&:last-child td': {
              borderBottom: 'none',
            },
          }}
        />
      ),
    );

    return { Table, TableHead, TableBody, TableRow };
  }, []);

  const fixedHeaderContent = useCallback(() => {
    return (
      <MuiTableRow>
        {headers.map((header, index) => (
          <MuiTableCell
            key={index}
            sx={{
              ...headerCellStyles,
              width: columnWidth,
              // Add extra padding on the last header cell for copy button
              ...(index === headers.length - 1 && originalMarkdown ? { paddingRight: '48px' } : {}),
            }}
          >
            {header}
          </MuiTableCell>
        ))}
      </MuiTableRow>
    );
  }, [headers, headerCellStyles, originalMarkdown, columnWidth]);

  const itemContent = useCallback(
    (_index: number, row: string[]) => {
      return (
        <>
          {row.map((cell, cellIndex) => (
            <MuiTableCell
              key={cellIndex}
              sx={{
                ...cellStyles,
                width: columnWidth,
                color: variantProps.fontColor,
              }}
            >
              <ClampedText 
                text={<Markdown rehypePlugins={[rehypeRaw]}>{cell}</Markdown>} 
                lines={MAX_LINES} 
                textColor={variantProps.fontColor} 
              />
            </MuiTableCell>
          ))}
        </>
      );
    },
    [cellStyles, variantProps.fontColor, columnWidth],
  );

  if (headers.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        border: `1px solid ${colors.grey[120]}`,
        borderRadius: '8px',
        overflow: 'hidden',
        // Show copy button on hover
        '&:hover .table-copy-button': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}
    >
      <TableVirtuoso
        ref={virtuosoRef}
        data={rows}
        components={components}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={itemContent}
        style={{
          height: '100%',
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.grey[300]} transparent`,
        }}
      />

      {/* Copy button */}
      {originalMarkdown && (
        <Box
          className="table-copy-button"
          sx={{
            position: 'absolute',
            top: '16px',
            right: '8px',
            zIndex: 10,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
            backgroundColor: colors.grey[100],
            borderRadius: '50%',
          }}
        >
          <VirtualizedTableCopyButton markdown={originalMarkdown} headers={headers} rows={rows} />
        </Box>
      )}
    </Box>
  );
};
